import { useState } from 'react'
import { CheckIcon, ChevronDownIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { IResolvedParticipant } from '../../../Model/Project/IResolvedParticipant.js'
import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.js'
import type { IUser } from '../../../Model/User/IUser.js'
import { ProjectParticipantBadge } from './ProjectParticipantBadge.js'
import { useDispatch } from 'react-redux'
import Actions from '../../../Flux/index.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'

type ParticipantsTableProps = {
  participants: IResolvedParticipant[] | 'LOADING'
  project: IEvaluationProject
  currentUser?: IUser
  readOnly: boolean
}

const ParticipantsTable = ({ participants, project, currentUser, readOnly }: ParticipantsTableProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const dispatch = useDispatch()

  // Check if the current user can modify permissions

  // Check if a participant's permissions can be modified
  const canModifyParticipant = (participant: IResolvedParticipant) => {
    if (readOnly) return false
    if (participant.participant.userId === currentUser?.id) return false // Can't modify own permissions
    if (participant.participant.userId === project.ownerUser) return false // Can't modify owner's permissions
    return true
  }

  // Handle toggling write permissions
  const handleTogglePermission = (participant: IResolvedParticipant) => {
    if (!canModifyParticipant(participant) || !project.version) return
    if (!participant.participant.id) {
      console.error('Participant ID is undefined aborting')
      return
    }

    if (!participant.participant.version) {
      console.error('Participant Version is undefined! aborting')
      return
    }

    dispatch(
      Actions[projectSlice.name].updateParticipantRequest({
        projectId: project.id,
        participantId: participant.participant.id,
        version: participant.participant.version,
        mayEdit: !participant.participant.mayEdit
      })
    )
  }

  const handleDeleteParticipant = (participant: IResolvedParticipant) => {
    if (!canModifyParticipant(participant) || !project.version) return
    if (!participant.participant.id) {
      console.error('Participant ID is undefined aborting')
      return
    }
    if (window.confirm('Really remove this participant?')) {
      dispatch(
        Actions[projectSlice.name].removeParticipantRequest({
          projectId: project.id,
          participantId: participant.participant.id
        })
      )
    }
  }

  if (participants == 'LOADING') {
    return <div className="mb-6">Loading...</div>
  }

  return (
    <div className="mb-6">
      {/* Collapsible Header */}
      <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg shadow-md">
        <div className="flex items-center cursor-pointer grow" onClick={() => setIsExpanded(!isExpanded)}>
          <h2 className="text-lg font-semibold">Participants</h2>
          <div className="flex mx-10 gap-2 w-full">
            {participants?.map((participant, index) => (
              <ProjectParticipantBadge mayEdit={participant.participant.mayEdit} user={participant.user} key={index} />
            ))}
          </div>
          <div>
            <ChevronDownIcon className={`w-4 h-4 animate-spin-180 ${isExpanded ? 'rotate-180' : ''} transition-all transform`} />
          </div>
        </div>
      </div>

      {/* Expanded Table */}
      {isExpanded && participants?.length > 0 && (
        <div className="overflow-x-auto mt-3 shadow-inner rounded-md">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Email</th>
                <th>Organization</th>
                <th>May Edit</th>
                {!readOnly && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {participants.map((participant, index) => (
                <tr key={index}>
                  <td>{participant.user.email}</td>
                  <td>{participant.user.org.name}</td>
                  <td>{participant.participant.mayEdit ? <CheckIcon className="w-4 h-4 text-green-500" /> : <XMarkIcon className="w-4 h-4 text-red-500" />}</td>
                  {!readOnly && (
                    <td className="flex justify-between">
                      {canModifyParticipant(participant) ? (
                        <>
                          <button
                            className={`btn btn-sm ${participant.participant.mayEdit ? 'btn-error' : 'btn-success'}`}
                            onClick={() => handleTogglePermission(participant)}
                          >
                            {participant.participant.mayEdit ? 'Remove Write Access' : 'Grant Write Access'}
                          </button>
                          <div className="max-h-8 h-8 flex items-center">
                            <TrashIcon
                              className="text-error h-8 w-8 cursor-pointer"
                              onClick={() => handleDeleteParticipant(participant)}
                            />
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400 italic">
                          {participant.participant.userId === currentUser?.id
                            ? "Can't modify own permissions"
                            : participant.participant.userId === project.ownerUser
                              ? "Can't modify owner's permissions"
                              : 'No permission to modify'}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default ParticipantsTable
